import { nextTick } from "vue";
import router from "@/router";
import store from "@/store";
import OrderService from "@/services/order.service";
/**
 * Go to member login
 */
export const gotoMemberLogin = (from = "", queue = false, action = "") => {
  // Fall back to route params / localStorage (written by router.beforeEach) in case
  // this runs before App.vue has populated the store (e.g. 401 during initial load)
  var merchantCode = store.state.merchantCode
    || router.currentRoute.value.params.merchant_code
    || localStorage.getItem("MERCHANT_CODE")
    || "";
  var branchCode = store.state.branchCode
    || router.currentRoute.value.params.branch_code
    || localStorage.getItem("BRANCH_CODE")
    || "";
  var tableCode = router.currentRoute.value.params.table_code
    || router.currentRoute.value.query.table_code
    || sessionStorage.getItem('TABLE_CODE')
    || new URLSearchParams(window.location.search).get('table_code')
    || "";
  var type = router.currentRoute.value.params.type || "";

  localStorage.removeItem(localStorageName("GUEST_MODE"));

  // Navigate to the internal MOO sign-in screen (no longer redirect to the member site)
  var params = { merchant_code: merchantCode };
  if (type) params.type = type;
  if (branchCode) params.branch_code = branchCode;
  if (tableCode) params.table_code = tableCode;

  var query = { branch_code: branchCode };
  if (tableCode) query.table_code = tableCode;
  if (from) query.from = from;
  if (queue) query.queue = "true";
  if (action) query.action = action;

  router.push({ name: "SignIn", params, query });
};

export const localStorageName = (name) => {
    return name + '_' + store.state.merchantCode + '_' + store.state.branchCode;
}

/**
 * Dining option the customer picked for this session (1 = take away, 2 = dine in).
 * item-detail needs it to hide modifiers that only apply to the other option;
 * localStorage holds it as a string, and nothing picked yet means dine in.
 */
export const getDiningOption = () => {
    const saved = Number(localStorage.getItem(localStorageName('DINING_OPTION')));
    return saved === 1 || saved === 2 ? saved : 2;
}

/**
 * Indexes of the cart items that carry a chosen modifier restricted to the other
 * dining option (item-detail tags each modifier group with its dining_option:
 * 0 = both, 1 = take away, 2 = dine in). Covers the item's own groups, the groups
 * of each chosen choice item, and one level of sub-modifiers under either. Items
 * added before item-detail returned dining_option have nothing to check and pass.
 */
export const getCartItemIndexesInvalidForDiningOption = (cartItems, diningOption) => {
    const option = Number(diningOption);

    const hasInvalidGroup = (groups) => (groups || []).some((group) => {
        const selectedItems = (group.items || []).filter((item) => item.selected);
        if (!selectedItems.length) {
            return false;
        }

        const groupOption = Number(group.dining_option);
        if ((groupOption === 1 || groupOption === 2) && groupOption !== option) {
            return true;
        }

        return selectedItems.some((item) => item.item_in_mog && hasInvalidGroup(item.item_in_mog.modifier_group));
    });

    return (cartItems || []).reduce((indexes, cartItem, index) => {
        const choiceItems = (cartItem.choice_selected && cartItem.choice_selected.items) || [];
        const invalid = hasInvalidGroup(cartItem.modifier_group)
            || choiceItems.some((choiceItem) => choiceItem.item_detail && hasInvalidGroup(choiceItem.item_detail.modifier_group));

        if (invalid) {
            indexes.push(index);
        }

        return indexes;
    }, []);
}

export const getMerchantCode = () => {
    return store.state.merchantCode;
}

/**
 * M899 — bring the first still-missing compulsory field back into view when a
 * form refuses to submit.
 *
 * MOO screens run well past one viewport: the buyer's name and phone sit at the
 * top of the cart while Pay sits at the bottom, so a merchant-side message that
 * renders 800px above the button reads as "the button does nothing". Screens
 * opt in by putting `is-invalid` on the element they already render for a failed
 * field; the first visible one in document order is the first problem, and this
 * scrolls to it and focuses the control it belongs to.
 *
 * Ported from M897 (merchant portal), minus the multiselect/file-input cases —
 * MOO has neither.
 *
 * `selector` lets a screen point at its own marker instead: the item-detail
 * modal has no inputs at all, so it anchors on the red "Select one" hint a
 * compulsory modifier group already renders while it is short of its minimum.
 * `highlight` names an ancestor to ring in place of taking focus — for a group
 * of options there is nothing to put a caret in, and without a ring a customer
 * whose modal does not need to scroll would see no answer to the tap at all.
 */
export const scrollToFirstInvalid = ({ selector = ".is-invalid", highlight = null } = {}) => {
  nextTick(() => {
    // Whatever was ringed last time answered the previous submit, not this one.
    clearHighlights();

    // A modal sits on top of the screen that opened it, and that screen may
    // still be showing errors of its own from an earlier attempt. Search only
    // inside the modal on top, or the page would scroll behind the dialog.
    const openModals = Array.from(
      document.querySelectorAll(".modal.show")
    ).filter(isFieldVisible);
    const scope = openModals.length ? openModals[openModals.length - 1] : document;

    const firstError = Array.from(scope.querySelectorAll(selector)).find(
      isFieldVisible
    );
    if (!firstError) return;

    firstError.scrollIntoView({ behavior: "smooth", block: "center" });

    if (highlight) {
      highlightControl(firstError.closest(highlight) || firstError);
      return;
    }

    const owner = controlFor(firstError);
    if (!owner) return;

    if (owner.matches(FOCUSABLE_FIELD)) {
      // preventScroll: the smooth scroll above is already under way; letting the
      // focus scroll too snaps the page there instantly and then drifts.
      owner.focus({ preventScroll: true });
    } else {
      // Not something that takes a caret (a date picker trigger, a styled block
      // standing in for an input) — ring it so it is still obvious which field
      // stopped the submit.
      highlightControl(owner);
    }
  });
};

const ERROR_HIGHLIGHT = "field-needs-attention";

const clearHighlights = () =>
  Array.from(document.querySelectorAll("." + ERROR_HIGHLIGHT)).forEach((el) =>
    el.classList.remove(ERROR_HIGHLIGHT)
  );

const highlightControl = (el) => {
  el.classList.add(ERROR_HIGHLIGHT);
  // It has done its job once the customer reaches the field; leaving the ring on
  // while they type would just read as a second, stale error.
  const clear = () => el.classList.remove(ERROR_HIGHLIGHT);
  el.addEventListener("pointerdown", clear, { once: true });
  el.addEventListener("focusin", clear, { once: true });
};

// Covers display:none, an ancestor that is hidden, and the `d-none` error spans
// some screens toggle by hand — all of them report no client rects.
const isFieldVisible = (el) => el.getClientRects().length > 0;

const FOCUSABLE_FIELD =
  'input:not([type="hidden"]):not([disabled]):not([readonly]), ' +
  "textarea:not([disabled]):not([readonly]), select:not([disabled])";

const controlFor = (el) => {
  if (el.matches(FOCUSABLE_FIELD)) return el;
  // The error text usually sits next to its input, but MOO groups several fields
  // under one `.form-group`, so widen the search one ancestor at a time until a
  // control turns up.
  let scope = el.parentElement;
  for (let depth = 0; scope && depth < 3; depth++, scope = scope.parentElement) {
    const controls = Array.from(scope.querySelectorAll(FOCUSABLE_FIELD)).filter(
      isFieldVisible
    );
    if (!controls.length) continue;
    // A message always follows the control it belongs to, so the one to blame is
    // the LAST control above it — not the first in the container, or a wide
    // container hands back the field at the top of the form, which the customer
    // has usually already filled in.
    const above = controls.filter(
      (c) => c.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    return above.length ? above[above.length - 1] : controls[0];
  }
  return null;
};

/**
 * MTH080: MERCHANT_ID can go missing on a page that otherwise loads fine — e.g.
 * Profile.vue's handleLogout() does localStorage.clear() then a plain SPA
 * router.push (no reload), and App.vue only validates/writes MERCHANT_ID once,
 * on its own initial mount. Every endpoint behind jwt.merchant_id_and_code needs
 * it, so any call that fires automatically (not gated behind a user action taken
 * after the page already proved it works) should await this first. Called from
 * the axios request interceptor (setupInterceptors.js) ahead of every request,
 * so concurrent requests for the SAME merchant/branch share one in-flight repair
 * instead of each firing its own check-merchant-and-branch call. Keyed by the
 * namespaced key (not a single shared lock) so a concurrent repair for a
 * different branch — e.g. right after switching tables/branches in the same
 * tab — can't hand back the wrong branch's result.
 */
const merchantIdRepairInFlight = new Map();
export const ensureMerchantId = async (branchCode) => {
  if (!branchCode || localStorage.getItem(localStorageName('MERCHANT_ID'))) {
    return;
  }
  const key = localStorageName('MERCHANT_ID');
  if (merchantIdRepairInFlight.has(key)) {
    return merchantIdRepairInFlight.get(key);
  }
  const repairPromise = (async () => {
    try {
      const resp = await OrderService.checkMerchantAndBranch({ branch_code: branchCode });
      if (resp.data.result.isSuccess) {
        localStorage.setItem(key, resp.data.data.merchant_id ?? 0);
      }
    } catch (e) {
      // swallow — the caller's own request will surface its own error toast
    } finally {
      merchantIdRepairInFlight.delete(key);
    }
  })();
  merchantIdRepairInFlight.set(key, repairPromise);
  return repairPromise;
};

/**
 * Retry an async check on a delay until it returns a truthy value or attempts run out.
 * Needed because Stripe's redirect back to the app and its webhook delivery are
 * independent, unordered events - a single check right after redirect can race
 * ahead of the webhook (and any queue processing behind it).
 */
export const pollUntil = async (checkFn, { maxAttempts = 5, delayMs = 3000 } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await checkFn();
    if (result) {
      return result;
    }
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
  return null;
};

/**
 * Poll our backend for whether a specific transaction has been confirmed paid,
 * retrying to ride out webhook/queue lag (see pollUntil above).
 */
export const pollTransactionPaid = async (branchCode, transactionId, pollOptions) => {
  const dataForm = { branch_code: branchCode, transaction_id: transactionId };
  return !!(await pollUntil(
    async () => {
      try {
        const response = await OrderService.checkTransactionPaid(dataForm);
        return response.data.result.isSuccess && response.data.data.is_paid;
      } catch (e) {
        return false;
      }
    },
    pollOptions || { maxAttempts: 3, delayMs: 2000 }
  ));
};

/**
 * Send the browser to the payment-success page for a transaction. merchantCode is
 * resolved with the same store -> route -> localStorage fallback chain gotoMemberLogin
 * above uses, rather than trusting a caller-captured value - store.state.merchantCode
 * is populated asynchronously by App.vue after a fresh page load/reload, and callers of
 * this function run exactly in that window (right after a reload/back navigation during
 * checkout), so a component's own snapshot of it can still be empty at this point.
 */
export const redirectToPaymentSuccess = (branchCode, transactionId, { tableInfo, tableCode, extraQuery } = {}) => {
  var merchantCode = store.state.merchantCode
    || router.currentRoute.value.params.merchant_code
    || localStorage.getItem("MERCHANT_CODE")
    || "";
  var url = location.protocol + "//" + location.host + "/" + merchantCode;
  url += tableInfo
    ? "/QR/" + branchCode + "/" + tableCode + "/payment-success/" + transactionId
    : "/" + branchCode + "/payment-success/" + transactionId;
  if (extraQuery) {
    url += "?" + extraQuery;
  }
  window.location.href = url;
};

/**
 * Discard the PENDING_PAYMENT_TXN marker once a payment attempt is definitively known
 * to have NOT gone through (e.g. Stripe/checkCheckoutSessionStripe itself confirms
 * cancelled) - otherwise it lingers for up to 3 hours, and clearCartIfPendingPaymentPaid
 * keeps re-checking it (harmlessly, but pointlessly) on every subsequent page load.
 * Never call this when the outcome is merely unknown (e.g. a network error while
 * verifying) - only on an explicit, confirmed "not paid".
 */
export const clearPendingPaymentMarker = () => {
  localStorage.removeItem(localStorageName("PENDING_PAYMENT_TXN"));
};

/**
 * Reconcile a cart against a payment that was started (PENDING_PAYMENT_TXN marker set
 * right before an off-site Stripe/AllInPay redirect) but whose outcome the app never
 * got to observe directly - e.g. the browser reloaded, lost connection, or the customer
 * navigated straight back instead of following the gateway's own redirect. This is the
 * fallback of last resort: it works no matter how the customer ends up back in the app
 * (QR rescan into Order.vue, or navigating straight back into MyCart.vue), since it only
 * depends on the marker in localStorage, not on any query params from a redirect.
 *
 * On a confirmed paid match, clears the cart and takes the customer to the payment-success
 * page for that transaction (so they see their order went through instead of landing back
 * on an emptied cart/menu with no explanation) and returns true. Returns false otherwise -
 * cart/pending marker are only ever touched on a confirmed match, never on "not paid" or error.
 */
export const clearCartIfPendingPaymentPaid = async (cartName, branchCode, options = {}) => {
  const { pollOptions, tableInfo, tableCode } = options;
  const key = localStorageName("PENDING_PAYMENT_TXN");
  let pending;
  try {
    pending = JSON.parse(localStorage.getItem(key, null));
  } catch (e) {
    // Corrupt value - discard rather than letting a malformed marker crash callers.
    localStorage.removeItem(key);
    return false;
  }

  if (!pending || !pending.transaction_id) {
    return false;
  }

  // Kept short on purpose: this only needs to outlast realistic webhook/queue lag
  // (seconds to a few minutes - see ProcessWebhookJob), not an entire visit. A long
  // window risks silently wiping out a cart the customer has since edited into
  // something different from what this transaction was actually for.
  const PENDING_PAYMENT_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
  if (Date.now() - new Date(pending.created_at).getTime() > PENDING_PAYMENT_EXPIRY_MS) {
    localStorage.removeItem(key);
    return false;
  }

  const isPaid = await pollTransactionPaid(branchCode, pending.transaction_id, pollOptions);
  if (!isPaid) {
    return false;
  }

  localStorage.removeItem(cartName);
  localStorage.removeItem(localStorageName("SELECTED_REWARD"));
  localStorage.removeItem(localStorageName("PROMO_CODE_APPLIED"));
  localStorage.removeItem(localStorageName("DINING_OPTION"));
  localStorage.removeItem(key);

  redirectToPaymentSuccess(branchCode, pending.transaction_id, { tableInfo, tableCode });
  return true;
};

/**
 * The tier's "Condition to achieve tier" (merchant Setting > Tier), evaluated
 * client-side because the API exposes no eligibility flag of its own.
 *
 * Two of the three conditions compare merchant_member columns directly. The
 * spending one has no number to compare against — neither tier-info nor
 * get-data-member-moo returns the member's amount spent — so it reads the
 * verdict the API already computed into note_text (_processLevelData writes
 * "…away to achieve this tier" while the spend target is unmet). That makes
 * this check dependent on that wording: if the API rewords or translates
 * note_text, the spending condition silently stops being detected.
 *
 * This is a UI gate only. The API still grants the tier to anyone who pays —
 * blocking that needs the guard on the server side.
 */
export const checkTierAchieveConditions = (tier, merchantMember) => {
  const reasons = [];
  if (!tier) {
    return { eligible: false, reasons };
  }
  const member = merchantMember || {};

  if (
    tier.spending_achieve_tier > 0 &&
    (tier.note_text || "").includes("away to achieve")
  ) {
    reasons.push(
      tier.upgrade_tier_by_admissions
        ? "Visit " + tier.visit_achieve_tier_format + " times to achieve this tier"
        : "Spend " + tier.spending_achieve_tier_format + " to achieve this tier"
    );
  }

  if (tier.share_full_profile && !member.share_info_profile) {
    reasons.push("Share your full profile");
  }

  if (
    tier.already_achieved_a_paid_tier_previously &&
    !member.already_achieved_a_paid_tier_previously
  ) {
    reasons.push("Achieve a paid tier previously");
  }

  return { eligible: reasons.length === 0, reasons };
};

/**
 * Display fields for the "redeemed" sheet, carried across a card-payment
 * redirect.
 *
 * A balance redeem still holds the voucher in component state when the sheet
 * opens; a card redeem comes back on a freshly built component with nothing but
 * the gateway's query string, so the same sheet had no name, kind or price to
 * show and silently degraded to a bare sentence. Stashing the already-formatted
 * strings before leaving keeps both paths reporting the same receipt.
 *
 * Read-once: a leftover entry from an abandoned checkout would otherwise be
 * attached to whatever is redeemed next.
 */
export const stashPendingRedeem = (summary) => {
  try {
    localStorage.setItem(
      localStorageName("REDEEM_PENDING"),
      JSON.stringify(summary)
    );
  } catch (e) {
    // Private mode / storage full: the sheet falls back to its short form.
  }
};

export const takePendingRedeem = () => {
  const key = localStorageName("REDEEM_PENDING");
  try {
    const raw = localStorage.getItem(key);
    localStorage.removeItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * True when this page load is the landing after a hosted-checkout redirect.
 *
 * Matters for "back": the browser's previous entry is then the gateway's own
 * page, so router.go(-1) walks the member out of the app and into Stripe's
 * checkout. A screen that can be landed on this way has to push somewhere
 * inside the app instead.
 */
export const isGatewayReturn = (query = {}) =>
  !!(query.session_id || query.airwallex_intent_id || query.exchange);
